import { LocalizationString } from '@celito.clients/assets';
import {
  AssignmentStatusEnum,
  ButtonTypes,
  CompletionTypeEnum,
  TaskStatusEnum,
  TrainingAssignmentTypeEnum,
} from '@celito.clients/enums';
import { UserContext } from '@celito.clients/provider';
import { fetchObjectTaskInfo } from '@celito.clients/services';
import {
  CompleteTrainingButton,
  CustomButton,
  Icon,
} from '@celito.clients/shared';
import { Task } from '@celito.clients/types';
import { Label, Skeleton, SkeletonItem } from '@fluentui/react-components';
import cn from 'classnames';
import { convert } from 'html-to-text';
import { useContext, useEffect, useState } from 'react';

import { trainingAssignmentObjectName } from '../../document/documents-view-page/config';
import { documentSignOffTag } from '../config';
import { getQuizAttempts } from '../services';
import classes from '../taking-assingment.module.css';
import { TakingAssingmetViewProps } from '../taking-assingment.view';
import { QuizAttemptsData } from '../types';

interface CompleteBarProps extends TakingAssingmetViewProps {}

const takingTrainingTag = documentSignOffTag;

// ─── Types ───────────────────────────────────────────────────────────

interface ButtonData {
  buttonTitle: string;
  onClick?: () => void;
  isAdminComplete?: boolean;
}

interface CompleteBarViewProps {
  isLoading: boolean;
  buttonData: ButtonData | null;
  assignmentData: CompleteBarProps['assignmentData'];
  trainingAssignmentType: CompleteBarProps['trainingAssignmentType'];
  courseData: CompleteBarProps['courseData'];
  taskResponse: Task | undefined;
}

// ─── View Component (pure render) ───────────────────────────────────

const CompleteBarView = ({
  isLoading,
  buttonData,
  assignmentData,
  trainingAssignmentType,
  courseData,
  taskResponse,
}: CompleteBarViewProps): JSX.Element | null => {
  const [showMore, setShowMore] = useState(false);

  const isCompleted = assignmentData?.status === AssignmentStatusEnum.Completed;

  const getTaskTitle = () => {
    if (taskResponse) {
      return taskResponse.label;
    }
    return LocalizationString.TASK_DATA_NOT_FOUND;
  };

  // Loading state: show skeleton
  if (isLoading) {
    return (
      <div className={classes.complete_bar}>
        {!isCompleted && (
          <div className={classes.task_info}>
            <div className={classes.clipboard_icon}>
              <SkeletonItem size={20} shape="circle" />
            </div>
            <div className={classes.parentbtn} style={{ flex: 1 }}>
              <div className={classes.topbtn}>
                <div
                  className={classes.labels}
                  style={{
                    display: 'flex',
                    gap: '8px',
                    alignItems: 'center',
                    width: '100%',
                  }}
                >
                  <SkeletonItem size={16} style={{ width: '80px' }} />
                  <SkeletonItem size={16} />
                </div>
              </div>
            </div>
          </div>
        )}
        <SkeletonItem
          size={40}
          style={{
            width: '140px',
            marginLeft: 'auto',
            display: 'block',
          }}
        />
      </div>
    );
  }

  // No button data after loading: don't render
  if (!buttonData) {
    return null;
  }

  // Loaded state: render actual content
  return (
    <div
      className={cn(classes.complete_bar, {
        [classes.complete_bar_no_task]: isCompleted,
      })}
    >
      {!isCompleted && (
        <div className={classes.task_info}>
          <div className={classes.clipboard_icon}>
            <Icon iconName="ClipboardTextRtl20Filled" />
          </div>
          <div className={classes.parentbtn}>
            <div className={classes.topbtn}>
              <div className={classes.labels}>
                <Label weight="semibold">
                  {LocalizationString.TASK_TITLE}:
                </Label>
                <Label>{getTaskTitle()}</Label>
              </div>

              {taskResponse ? (
                <button
                  className={classes.showHideText}
                  onClick={() => setShowMore(!showMore)}
                >
                  {!showMore ? 'Show More' : 'Show Less'}
                </button>
              ) : null}
            </div>
            {showMore ? (
              <div className={classes.descDiv}>
                <Label weight="semibold">
                  Description:{' '}
                  <span className={classes.descText}>
                    {taskResponse?.description
                      ? convert(taskResponse?.description)
                      : '-'}
                  </span>
                </Label>
              </div>
            ) : null}
          </div>
        </div>
      )}
      {buttonData.buttonTitle === 'Complete' ? (
        <CompleteTrainingButton
          trainingAssignmentData={assignmentData!}
          trainingAssignmentType={trainingAssignmentType}
          trainingPlanName={assignmentData?.course?.name || ''}
          showInHeaderActions={false}
        />
      ) : (
        <CustomButton
          buttonType={ButtonTypes.Primary}
          buttonTitle={buttonData.buttonTitle}
          onClick={buttonData.onClick}
          isLoading={false}
          iconPosition="before"
          disabled={
            ['Start Training', 'Review Material'].includes(
              buttonData.buttonTitle
            ) && !courseData
          }
        />
      )}
    </div>
  );
};

// ─── Container Component (logic/data) ───────────────────────────────

export const CompleteBar = ({
  assignmentData,
  trainingAssignmentType,
  isBizAdmin,
  startTraining,
  viewCertificate,
  user,
  courseData,
}: CompleteBarProps): JSX.Element | null => {
  const [taskState, setTaskState] = useState<{
    isLoading: boolean;
    taskResponse: Task | undefined;
  }>({ isLoading: true, taskResponse: undefined });

  const [quizAttempts, setQuizAttempts] = useState<
    QuizAttemptsData | undefined
  >(undefined);

  const { user: userContext } = useContext(UserContext);

  useEffect(() => {
    if (assignmentData) {
      let params: Record<string, string> | undefined;

      if (assignmentData?.status === AssignmentStatusEnum.Completed) {
        params = { status: TaskStatusEnum.Done };
        if (
          trainingAssignmentType === TrainingAssignmentTypeEnum.MyAssignments
        ) {
          params.tag = takingTrainingTag;
        }
      }
      setTaskState({ isLoading: true, taskResponse: undefined });
      fetchObjectTaskInfo(
        trainingAssignmentObjectName,
        assignmentData?.name,
        params
      )
        .then((res) => {
          setTaskState({ isLoading: false, taskResponse: res?.task });
        })
        .catch((_error) => {
          setTaskState({ isLoading: false, taskResponse: undefined });
        });
    }

    if (
      assignmentData?.status === AssignmentStatusEnum.InProgress &&
      assignmentData?.course?.completionType === CompletionTypeEnum.Quiz
    ) {
      getQuizAttemptsDetails();
    }
  }, []);

  const getQuizAttemptsDetails = async () => {
    try {
      const quizAttemptsData = await getQuizAttempts(
        assignmentData?.name ?? ''
      );
      setQuizAttempts(quizAttemptsData);
    } catch (error) {
      // Do nothing
    }
  };

  // ─── Permission checks (synchronous, no render needed) ───────────

  // Validate assignment data exists
  if (!assignmentData || !assignmentData?.course) {
    return null;
  }

  const isManagerRequired =
    userContext?.roles?.some((role) =>
      role.name.includes('ul_manager_role__a')
    ) && trainingAssignmentType !== TrainingAssignmentTypeEnum.MyAssignments;

  const isBizAdminViewingTeam =
    isBizAdmin &&
    trainingAssignmentType !== TrainingAssignmentTypeEnum.MyAssignments;

  // Biz admins and managers should not see task bar for PendingSignOff
  if (isBizAdminViewingTeam || isManagerRequired) {
    if (assignmentData?.status === AssignmentStatusEnum.PendingSignOff) {
      return null;
    }
  }

  // Managers should not see task bar for trainee statuses
  if (
    isManagerRequired &&
    (assignmentData?.status === AssignmentStatusEnum.Assigned ||
      assignmentData?.status === AssignmentStatusEnum.Overdue ||
      assignmentData?.status === AssignmentStatusEnum.Todo ||
      assignmentData?.status === AssignmentStatusEnum.InProgress)
  ) {
    return null;
  }

  // ─── Button data calculation ──────────────────────────────────────

  const getButtonData = (): ButtonData | null => {
    // Biz admin / manager logic
    if (isBizAdminViewingTeam || isManagerRequired) {
      if (assignmentData?.status === AssignmentStatusEnum.Completed) {
        return {
          buttonTitle: 'View Certificate',
          onClick: viewCertificate,
          isAdminComplete: true,
        };
      } else if (assignmentData?.status === AssignmentStatusEnum.UnAssigned) {
        return {
          buttonTitle: 'Complete',
          isAdminComplete: false,
        };
      }

      if (isBizAdmin) {
        if (assignmentData?.status === AssignmentStatusEnum.PendingSignOff) {
          return null;
        }
        return {
          buttonTitle: 'Complete',
          isAdminComplete: false,
        };
      }
    }

    // Owner / delegated user check (depends on taskResponse)
    if (
      taskState.taskResponse &&
      user?.name !== taskState.taskResponse?.ownerName &&
      !JSON.parse(taskState.taskResponse?.delegatedUserNames ?? '[]').includes(
        user?.name
      )
    ) {
      return null;
    }

    // Status-based logic
    if (assignmentData?.status === AssignmentStatusEnum.Completed) {
      return {
        buttonTitle: 'View Certificate',
        onClick: viewCertificate,
      };
    }

    if (
      assignmentData?.status === AssignmentStatusEnum.Assigned ||
      assignmentData?.status === AssignmentStatusEnum.Overdue ||
      assignmentData?.status === AssignmentStatusEnum.Todo
    ) {
      if (!isBizAdminViewingTeam) {
        return {
          buttonTitle: 'Start Training',
          onClick: () => startTraining(true, true),
        };
      }
    }

    if (assignmentData?.status === AssignmentStatusEnum.InProgress) {
      const isMaterialReviewNeeded =
        assignmentData?.course?.completionType === CompletionTypeEnum.Quiz &&
        quizAttempts &&
        !quizAttempts.isPassed &&
        !quizAttempts.hasQuizAttemptLeft;

      if (isMaterialReviewNeeded) {
        return {
          buttonTitle: 'Review Material',
          onClick: () => startTraining(true, true),
        };
      }

      if (
        assignmentData?.course?.document &&
        assignmentData?.isDocumentComplete
      ) {
        return {
          buttonTitle: 'Review Material',
          onClick: () => startTraining(true, false),
        };
      }

      return {
        buttonTitle: 'Review Material',
        onClick: () => startTraining(true, true),
      };
    }

    if (assignmentData?.status === AssignmentStatusEnum.PendingSignOff) {
      const delegatedUserNames = JSON.parse(
        taskState.taskResponse?.delegatedUserNames ?? '[]'
      );
      const isTrainer =
        user?.name === assignmentData?.trainer?.name ||
        (taskState.taskResponse?.tag === 'ta_trainer_sign_off' &&
          delegatedUserNames.includes(user?.name));

      if (isTrainer) {
        return {
          buttonTitle: 'Complete',
        };
      }
      return null;
    }

    return null;
  };

  // Consider all data loaded only when task info AND course data are ready
  const needsCourseData =
    assignmentData?.status === AssignmentStatusEnum.Assigned ||
    assignmentData?.status === AssignmentStatusEnum.Overdue ||
    assignmentData?.status === AssignmentStatusEnum.Todo ||
    assignmentData?.status === AssignmentStatusEnum.InProgress;

  const isAllDataReady =
    !taskState.isLoading && (!needsCourseData || !!courseData);

  const buttonData = isAllDataReady ? getButtonData() : null;

  // ─── Render ───────────────────────────────────────────────────────

  return (
    <CompleteBarView
      isLoading={!isAllDataReady}
      buttonData={buttonData}
      assignmentData={assignmentData}
      trainingAssignmentType={trainingAssignmentType}
      courseData={courseData}
      taskResponse={taskState.taskResponse}
    />
  );
};
